import { Box, Button, Chip, Typography } from '@mui/material'
import React, { useState } from 'react'
import { ICoupon } from '../../types/leaderboardTypes'
import { RewardItem } from '../../services/rewardsService'
import ExpiryLabel from './ExpiryLabel'

interface CouponCardProps {
  coupon?: ICoupon
  reward?: RewardItem
  onCardClick: () => void
  onShowInstructions?: (reward: RewardItem) => void
  type?: 'my-coupon' | 'redeem-points'
}

const CouponCard: React.FC<CouponCardProps> = ({
  coupon,
  reward,
  onCardClick,
  onShowInstructions,
  type = 'redeem-points',
}) => {
  const [isExpired, setIsExpired] = useState(false)
  
  // Use reward data if available, otherwise fall back to coupon
  const displayData = reward
    ? {
        image: reward.imageUrl, // Use actual imageUrl from API (can be null)
        title: reward.name,
        description: reward.shortDescription ?? '',
        pointsRequired: reward.pointsCost,
        amountCents: reward.amountCents,
        currency: reward.currency,
        expiryDate: coupon?.expiryDate,
        usageInstructions: reward.usageInstructions,
      }
    : coupon
      ? {
          image: coupon.image,
          title: coupon.title,
          description: coupon.description,
          pointsRequired: coupon.pointsRequired,
          amountCents: undefined,
          currency: undefined,
          expiryDate: coupon.expiryDate,
          redemptionInstructions: undefined,
        }
      : null

  if (!displayData) return null

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      maxWidth={350}
      onClick={onCardClick}
    >
      <Box
        width="100%"
        sx={{
          position: 'relative',
        }}
      >
        {displayData.image && (
          <Box
            sx={{
              border: 1,
              borderColor: 'grey.200',
              borderRadius: '16px',
              boxShadow: '0px 0px 2px 0px #1A1A1A40',
              aspectRatio: 5 / 2.9,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={displayData.image}
              alt={displayData.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'transform',
                filter: isExpired ? 'grayscale(100%)' : 'none',
                '&:hover': {
                  transform: 'scale(1.02)',
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        )}
        {reward?.cashLike && (
          <Chip
            label="Cash"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          />
        )}
      </Box>
      {type === 'my-coupon' && displayData.expiryDate && (
        <Box mt={1.5} sx={{ alignSelf: 'flex-start' }}>
          <ExpiryLabel date={displayData.expiryDate} setExpired={setIsExpired} />
        </Box>
      )}

      <Box mt={1.5} width="100%">
        <Typography variant="body2" maxWidth={'100%'} noWrap fontWeight={600}>
          {displayData.title}
        </Typography>
        {displayData.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            mt={0.5}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayData.description}
          </Typography>
        )}
        {type === 'redeem-points' && (
          <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
            {displayData.amountCents != null && (
              <Typography variant="body2" fontWeight={600} color="text.primary">
                Estimated value: ${(displayData.amountCents / 100).toFixed(2)} {displayData.currency}
              </Typography>
            )}
            {reward?.cashLike && reward.disableReason === 'SUBSCRIPTION_REQUIRED' && (
              <Typography variant="caption" color="text.secondary">
                Cash out with Explorer or VIP
              </Typography>
            )}
            {reward?.usageInstructions && onShowInstructions && (
              <Button
                variant="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onShowInstructions(reward)
                }}
                sx={{ px: 0, mt: 0.5, alignSelf: 'flex-start', textTransform: 'none', minWidth: 'auto' }}
              >
                How to redeem
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CouponCard
